import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Admin PWA web push contracts. The admin subscribes a device through
 * `/me/push/*`; the API then pushes bell notifications, WhatsApp messages and
 * WhatsApp calls to it, filtered by the user's own preferences below.
 */

/**
 * How much of an alert shows (everywhere - a web push cannot tell a locked
 * phone from an unlocked one; the phone's own setting handles that).
 *   FULL   - who it is from and what it says.
 *   SENDER - who it is from only ("New message from Rahul Sharma").
 *   HIDDEN - a generic line only ("New WhatsApp message").
 */
export const WEB_PUSH_PREVIEW_LEVELS = ['FULL', 'SENDER', 'HIDDEN'] as const;
export const WebPushPreviewSchema = z.enum(WEB_PUSH_PREVIEW_LEVELS);
export type WebPushPreview = z.infer<typeof WebPushPreviewSchema>;

export const WebPushPreferencesSchema = z.object({
  /** Bell notifications - the same ones the in-app inbox shows. */
  notifications: z.boolean(),
  whatsappMessages: z.boolean(),
  whatsappCalls: z.boolean(),
  preview: WebPushPreviewSchema,
});
export type WebPushPreferences = z.infer<typeof WebPushPreferencesSchema>;

/** What a user gets before they ever open the settings card. */
export const DEFAULT_WEB_PUSH_PREFERENCES: WebPushPreferences = {
  notifications: true,
  whatsappMessages: true,
  whatsappCalls: true,
  preview: 'SENDER',
};

export const UpdateWebPushPreferencesRequestSchema = WebPushPreferencesSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  { message: 'Nothing to update.' },
);
export type UpdateWebPushPreferencesRequest = z.infer<typeof UpdateWebPushPreferencesRequestSchema>;

/**
 * The browser's `PushSubscription.toJSON()`, plus a label for the device list.
 * Size caps are generous versions of what real push services hand out
 * (FCM / Apple endpoints are ~200-500 chars, p256dh 87, auth 22).
 */
export const WebPushSubscribeRequestSchema = z.object({
  endpoint: z.string().url().max(2048),
  keys: z.object({
    p256dh: z.string().min(16).max(256),
    auth: z.string().min(8).max(64),
  }),
  deviceLabel: z.string().trim().max(80).optional(),
});
export type WebPushSubscribeRequest = z.infer<typeof WebPushSubscribeRequestSchema>;

export const WebPushUnsubscribeRequestSchema = z.object({
  endpoint: z.string().url().max(2048),
});
export type WebPushUnsubscribeRequest = z.infer<typeof WebPushUnsubscribeRequestSchema>;

export const WebPushConfigSchema = z.object({
  /** False when the server-side kill-switch is off or keys are unavailable. */
  enabled: z.boolean(),
  /** VAPID public key (base64url) the browser subscribes with. */
  publicKey: z.string().nullable(),
});
export type WebPushConfig = z.infer<typeof WebPushConfigSchema>;

export const WebPushDeviceSchema = z.object({
  id: UuidSchema,
  endpoint: z.string(),
  deviceLabel: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  lastSuccessAt: IsoDateTimeSchema.nullable(),
});
export type WebPushDevice = z.infer<typeof WebPushDeviceSchema>;

export const WebPushTestResultSchema = z.object({
  /** Devices the push service accepted the test for. */
  sent: z.number().int().nonnegative(),
  /** Devices that failed (network / push-service error). */
  failed: z.number().int().nonnegative(),
  /** Dead devices removed while trying (uninstalled app, revoked permission). */
  removed: z.number().int().nonnegative(),
});
export type WebPushTestResult = z.infer<typeof WebPushTestResultSchema>;
